import { ecChanges, HandBack, upper, type JanusCtx, type LogRow, type Row, definitionNotHeld } from './context.js';
import { runPaoeEntry, type PaoeEntryStep, type PaoeEntryVerdict } from './paoe-entry.js';
import { encounterField } from '../../shared/array-types.js';
import { zoneRollIsAdverse } from '../zones/end-of-turn-zones.js';
import { statusItemId, statusRows } from '../statuses/expiry.js';
import { zoneIsPainted } from '../zones/zone-membership.js';
import { hasSurfaceTypeValue as surfaceValuePresent } from '../../shared/surface-geometry.js';

export type PaoeEntryFact = Pick<LogRow, 'roll_type_1' | 'roll_item_id' | 'roll_to'>
  & Partial<Pick<LogRow, 'roll_by' | 'rolls_full' | 'roll_type_2'>> & { redacted?: true; time?: readonly number[] };
export interface PaoeEntryRecords { turn: string; rows: readonly PaoeEntryFact[]; surfaceOnly?: true;
  surfaceEntryHistoryIncomplete?: true; surfaceEntryUncertain?: { surfaces: readonly string[]; creatures: readonly string[] };
  surfaceEntryMoveOnly?: true; surfaceEntryMoveRows?: readonly PaoeEntryFact[];
  surface_entry_log_receipt?: 'no-prior-check-this-turn' }
export const PAOE_ENTRY_RECORD = 'paoe_entry_once';
/** A mixed route retains ordinary-zone history and the surface instance/move budget. */
export function combinePaoeEntryRecords(legacy: PaoeEntryRecords, surface: PaoeEntryRecords): PaoeEntryRecords {
  return { ...legacy, rows: [...legacy.rows, ...surface.rows], surfaceEntryHistoryIncomplete: surface.surfaceEntryHistoryIncomplete,
    surfaceEntryUncertain: surface.surfaceEntryUncertain, surfaceEntryMoveOnly: surface.surfaceEntryMoveOnly,
    surfaceEntryMoveRows: surface.surfaceEntryMoveRows, surface_entry_log_receipt: surface.surface_entry_log_receipt };
}
/** Known repeats resolve silently; only uncertain history emits a visible receipt. */
export type PaoeEntryOnceVerdict = PaoeEntryVerdict & { surface_entry_skipped?: true; surface_entry_log_receipt?: 'no-prior-check-this-turn' };

/** Native uncertainty receipts carry face tokens, either decoded or JSON-encoded by log delivery. */
export function surfaceEntrySkipStamp(row: PaoeEntryFact | null): string | null {
  if (!row || row.roll_type_1 !== 'surface_entry_skipped' || row.roll_type_2 !== 'history-incomplete') return null;
  let tokens: unknown = row.rolls_full;
  if (typeof tokens === 'string') try { tokens = JSON.parse(tokens); } catch { return null; }
  if (!Array.isArray(tokens)) return null;
  const turn = tokens.find(token => Array.isArray(token) && token[0] === 'Turn')?.[1];
  const surface = tokens.find(token => Array.isArray(token) && token[0] === 'Surface')?.[1];
  return typeof turn === 'string' && typeof surface === 'string' ? `${turn}|${upper(surface)}` : null;
}

/** A surface_type layer counts only when it has a value, including on inherited definitions. */
export function hasSurfaceTypeValue(read: Pick<JanusCtx['read'], 'value'>, definition: Row | null): boolean {
  const value = definition && read.value(definition, 'surface_type');
  return surfaceValuePresent(value);
}

/** PHP aoe_helpers.php:41–46 defaults an absent campaign's six roll-time fields to zero. */
export function paoeEntryTime(clock: ReturnType<JanusCtx['read']['campaignClock']>): number[] {
  return (['year', 'month', 'day', 'hour', 'minute', 'second'] as const).map(unit => clock?.[unit] ?? 0);
}

/** One shared entry/end-turn budget; outside combat, match the six legacy campaign-clock fields exactly. */
export function paoeEntryTurn(encounter: Row, clock: ReturnType<JanusCtx['read']['campaignClock']>): string {
  if (Number(encounterField(encounter, 'in_combat') ?? 0) === 1) return JSON.stringify(['turn', upper(encounterField(encounter, 'active_combat_id')),
    Number(encounterField(encounter, 'current_round') ?? 0), upper(encounterField(encounter, 'current_encounter_creature_id_turn'))]);
  return JSON.stringify(['time', ...paoeEntryTime(clock)]);
}

/** The same saved raw entry row is read and written by walking and teleport landing. */
export function runPaoeEntryOnce(ctx: JanusCtx, step: PaoeEntryStep, records: PaoeEntryRecords): PaoeEntryOnceVerdict {
  const definition = ctx.read.definition(step.abilityId);
  const surface = hasSurfaceTypeValue(ctx.read, definition);
  // Painted walls retain their own contact gate; the general entry budget never suppresses them.
  const holders = [ctx.encounter, ...ctx.members.values(), ...(Array.isArray(ctx.encounter.encounter_inventories_arr) ? ctx.encounter.encounter_inventories_arr as Row[] : [])];
  if (!surface && holders.some(holder => statusRows(holder, 'target').some(status => upper(status.status_connected_id) === upper(step.statusConnectedId)
    && (upper(status.status_origin_id) || statusItemId(status)) === upper(step.abilityId)
    && zoneIsPainted({ status, key: '', holderId: '' })))) return runPaoeEntry(ctx, step);
  const turn = paoeEntryTurn(ctx.encounter, ctx.read.campaignClock());
  if (records.turn !== turn) throw new HandBack('paoe-entry:turn-records-stale');
  if (!surface && records.surfaceOnly) throw new HandBack('paoe-entry:turn-records-not-held');
  const seen = paoeEntryAlreadyResolved(ctx, step, records, surface);
  if (seen && surface && !surfaceEntryChecked(ctx, step, records) && uncertainSurfaceEntry(step, records)) {
    // Uncertainty is visible once per exact creature/surface/turn, including across moves and reloads.
    const identity = [['Turn', turn], ['Surface', upper(step.statusConnectedId)]];
    if (![...surfaceEntryRows(records), ...ctx.rows].some(row => surfaceEntrySkipStamp(row) === `${turn}|${upper(step.statusConnectedId)}`
      && upper(row.roll_item_id) === upper(step.abilityId) && upper(row.roll_to) === upper(step.enteringId)))
      ctx.rows.push({ roll_type_1: 'surface_entry_skipped', roll_type_2: 'history-incomplete',
      roll_item_id: upper(step.abilityId), roll_by: upper(step.casterId), roll_to: upper(step.enteringId),
      roll_private: ctx.rollPrivate as 0 | 1 | 2, roll_result: 0, rolls_full: identity });
  }
  if (seen) return { kind: 'resolved', ...(surface ? { surface_entry_skipped: true as const } : {}), ...(surface ? { surface_entry_log_receipt: records.surface_entry_log_receipt } : {}), rows: ctx.rows, ec: ecChanges(ctx), statusesAdded: ctx.statusesAdded, statusesRemoved: ctx.statusesRemoved,
    charges: ctx.chargesChanged, playerCharactersChanged: ctx.playerCharactersChanged ?? [], inventory: ctx.inventoryChanged,
    deathTail: ctx.deathTail ?? [], concentrationBreaks: ctx.concentrationBreaks ?? [], statusStacks: ctx.statusStacks ?? [] };
  const verdict = runPaoeEntry(ctx, step);
  recordSurfaceEntryCheckedThisTurn(ctx, step);
  return surface && records.surface_entry_log_receipt ? { ...verdict, surface_entry_log_receipt: records.surface_entry_log_receipt } : verdict;
}

/** Record a resolved entry attempt, even when its effect does nothing or its Balance check fails. Ignition can call this directly. */
export function recordSurfaceEntryCheckedThisTurn(ctx: Pick<JanusCtx, 'encounter' | 'read' | 'rows' | 'rollPrivate'>, step: PaoeEntryStep): LogRow {
  const stamp = `${paoeEntryTurn(ctx.encounter, ctx.read.campaignClock())}|${upper(step.statusConnectedId)}`;
  const marker: LogRow = { roll_type_1: PAOE_ENTRY_RECORD, roll_type_2: null, roll_item_id: upper(step.abilityId), roll_by: upper(step.casterId),
    roll_to: upper(step.enteringId), roll_result: 0, roll_private: ctx.rollPrivate as 0 | 1 | 2, rolls_full: stamp };
  ctx.rows.push(marker);
  return marker;
}

/** Shared held-log lookup for entry and End Turn; never queries storage or draws dice. */
export function paoeEntryAlreadyResolved(ctx: Pick<JanusCtx, 'encounter' | 'read'> & { rows?: readonly LogRow[] },
  step: PaoeEntryStep, records: PaoeEntryRecords, surfaceEntry = false): boolean {
  const turn = paoeEntryTurn(ctx.encounter, ctx.read.campaignClock());
  if (records.turn !== turn) throw new HandBack('paoe-entry:turn-records-stale');
  const stamp = `${turn}|${upper(step.statusConnectedId)}`;
  const definition = ctx.read.definition(step.abilityId);
  if (!definition) definitionNotHeld('paoe-entry:definition-not-held', step.abilityId);
  // Surface ENTRY has an instance budget. A roll from a different spill of the same ability cannot spend it.
  // End Turn deliberately retains the legacy lookup below; its existing entry/end-turn behavior is unchanged.
  if (surfaceEntry) return surfaceEntryChecked(ctx, step, records) || uncertainSurfaceEntry(step, records);
  const rollType = upper(ctx.read.value(definition, 'roll_type'));
  const contest = rollType === 'AD80D31D-898B-66CE-0894-1335D11B1529'
    || (rollType === '77A044B7-7041-CA96-3C63-BB718B15A364' && ctx.read.has(definition, 'skill_roll_contestee'));
  const time = paoeEntryTime(ctx.read.campaignClock());
  return [...records.rows, ...(ctx.rows ?? [])].some(row => row.roll_type_1 !== 'surface_entry_skipped' && upper(row.roll_item_id) === upper(step.abilityId) && (
    'redacted' in row && row.redacted ? upper(row.roll_to) === upper(step.enteringId)
      && (row.roll_type_1 === PAOE_ENTRY_RECORD ? row.rolls_full === stamp
        : zoneRollIsAdverse(rollType) && row.time?.length === 6 && row.time.every((part, i) => part === time[i]))
    : row.roll_type_1 === PAOE_ENTRY_RECORD ? row.rolls_full === stamp
      && upper(row.roll_by) === upper(step.casterId) && upper(row.roll_to) === upper(step.enteringId)
      : zoneRollIsAdverse(rollType) && upper(row.roll_by) === upper(contest ? step.enteringId : step.casterId)
        && upper(row.roll_to) === upper(contest ? step.casterId : step.enteringId)));
 }

function surfaceEntryChecked(ctx: Pick<JanusCtx, 'encounter' | 'read'> & { rows?: readonly LogRow[] },
  step: PaoeEntryStep, records: PaoeEntryRecords): boolean {
  const stamp = `${records.turn}|${upper(step.statusConnectedId)}`;
  return [...surfaceEntryRows(records), ...(ctx.rows ?? [])].some(row => row.roll_type_1 === PAOE_ENTRY_RECORD
    && row.rolls_full === stamp && upper(row.roll_item_id) === upper(step.abilityId) && upper(row.roll_to) === upper(step.enteringId));
}

function uncertainSurfaceEntry(step: PaoeEntryStep, records: PaoeEntryRecords): boolean {
  return records.surfaceEntryHistoryIncomplete === true
    && records.surfaceEntryUncertain?.surfaces.includes(upper(step.statusConnectedId)) === true
    && records.surfaceEntryUncertain.creatures.includes(upper(step.enteringId));
}

function surfaceEntryRows(records: PaoeEntryRecords): readonly PaoeEntryFact[] {
  return records.surfaceEntryMoveOnly ? records.surfaceEntryMoveRows ?? [] : records.rows;
}
